import { useMemo, useState } from 'react'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { Search } from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { OperationsListBody } from '@/components/operations/OperationsListBody'
import { useOperationsList } from '@/hooks/api/useOperationsList'

export const Route = createFileRoute('/operations/')({
    component: OperationsIndex,
})

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

function OperationsIndex() {
    const [id, setId] = useState('')
    const navigate = useNavigate()
    const valid = UUID_RE.test(id.trim())

    return (
        <div className="space-y-6">
            <div>
                <h1 className="text-2xl font-semibold">Operations</h1>
                <p className="text-muted-foreground text-sm">
                    Look up an operation by id, or browse recent activity on your authorized chains.
                </p>
            </div>

            <Card>
                <CardHeader>
                    <CardTitle>Look up by id</CardTitle>
                    <CardDescription>
                        Operation IDs are returned by every successful POST to the gaming service.
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    <form
                        className="flex gap-2"
                        onSubmit={(e) => {
                            e.preventDefault()
                            if (valid)
                                navigate({ to: '/operations/$id', params: { id: id.trim() } })
                        }}
                    >
                        <Input
                            value={id}
                            onChange={(e) => setId(e.target.value.trim())}
                            placeholder="00000000-0000-0000-0000-000000000000"
                            className="max-w-md font-mono"
                        />
                        <Button type="submit" disabled={!valid}>
                            <Search className="h-4 w-4" /> Open
                        </Button>
                    </form>
                </CardContent>
            </Card>

            <HistoryCard />
        </div>
    )
}

function HistoryCard() {
    const query = useOperationsList(25)
    const items = useMemo(() => query.data?.pages.flatMap((p) => p.operations) ?? [], [query.data])

    return (
        <Card>
            <CardHeader>
                <CardTitle>History</CardTitle>
                <CardDescription>
                    Recent operations from <code className="font-mono">GET /v1/operations</code>,
                    scoped to the chains your operator session is authorized for.
                </CardDescription>
            </CardHeader>
            <CardContent>
                <OperationsListBody query={query} items={items} />
            </CardContent>
        </Card>
    )
}
